const LOBBY_CACHE_TTL_MS = 5 * 60 * 1000;
const TENANT_CACHE_TTL_MS = 5 * 60 * 1000;

interface CachedEntry<T> {
  data: T;
  updatedAt: number;
}

function readCache<T>(key: string, ttlMs: number): T | undefined {
  if (typeof sessionStorage === 'undefined') return undefined;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return undefined;
    const entry = JSON.parse(raw) as CachedEntry<T>;
    if (!entry?.data || typeof entry.updatedAt !== 'number') return undefined;
    if (Date.now() - entry.updatedAt > ttlMs) {
      sessionStorage.removeItem(key);
      return undefined;
    }
    return entry.data;
  } catch {
    return undefined;
  }
}

function writeCache<T>(key: string, data: T): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    const entry: CachedEntry<T> = { data, updatedAt: Date.now() };
    sessionStorage.setItem(key, JSON.stringify(entry));
  } catch {
    // Quota exceeded or private browsing — ignore
  }
}

function readCacheUpdatedAt(key: string, ttlMs: number): number | undefined {
  if (typeof sessionStorage === 'undefined') return undefined;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return undefined;
    const entry = JSON.parse(raw) as CachedEntry<unknown>;
    if (typeof entry.updatedAt !== 'number') return undefined;
    if (Date.now() - entry.updatedAt > ttlMs) return undefined;
    return entry.updatedAt;
  } catch {
    return undefined;
  }
}

/**
 * `viewerKey` scopes the cache to the signed-in viewer.
 *
 * The lobby payload became PERSONALISED when `has_entry` was added to each game, so a cached
 * copy is only valid for the viewer it was fetched for. Without this, signing out and back in
 * as someone else inside one browser session would show the previous viewer's entered tiles
 * until the live fetch landed.
 *
 * Appended only when there IS a viewer, so an anonymous session keeps the exact key it has
 * always used and nothing already cached is orphaned.
 */
export function lobbyCacheKey(tenantSlug: string, lobbySlug?: string, viewerKey?: string): string {
  const base = `lobby:cache:${tenantSlug}:${lobbySlug ?? 'default'}`;
  return viewerKey ? `${base}:u:${viewerKey}` : base;
}

export function readLobbyCache<T = unknown>(
  tenantSlug: string,
  lobbySlug?: string,
  viewerKey?: string
): T | undefined {
  return readCache<T>(lobbyCacheKey(tenantSlug, lobbySlug, viewerKey), LOBBY_CACHE_TTL_MS);
}

export function writeLobbyCache(
  tenantSlug: string,
  lobbySlug: string | undefined,
  data: unknown,
  viewerKey?: string
) {
  writeCache(lobbyCacheKey(tenantSlug, lobbySlug, viewerKey), data);
}

export function readLobbyCacheUpdatedAt(
  tenantSlug: string,
  lobbySlug?: string,
  viewerKey?: string
) {
  return readCacheUpdatedAt(lobbyCacheKey(tenantSlug, lobbySlug, viewerKey), LOBBY_CACHE_TTL_MS);
}

export function tenantCacheKey(tenantSlug: string): string {
  return `lobby:tenant-cache:${tenantSlug}`;
}

export function readTenantCache<T = unknown>(tenantSlug: string): T | undefined {
  return readCache<T>(tenantCacheKey(tenantSlug), TENANT_CACHE_TTL_MS);
}

export function writeTenantCache(tenantSlug: string, data: unknown) {
  writeCache(tenantCacheKey(tenantSlug), data);
}

export function readTenantCacheUpdatedAt(tenantSlug: string) {
  return readCacheUpdatedAt(tenantCacheKey(tenantSlug), TENANT_CACHE_TTL_MS);
}
